import React, { useState, useEffect } from "react";
import styled from "styled-components";
import colors from "../../utils/constants/COLORS";
import shadows from "../../utils/constants/SHADOWS";
import typography from "../../utils/constants/TYPOGRAPHY";
import Loading from "../Loading/Loading";

// typography & colors

// What the button actually looks like
const variants = {
  basic: {
    default: {
      background: "#FFFFFF",
      color: "var(--carter-primary)",
      shadow: shadows.depth1,
      stroke: "#BBBFC3",
    },
    hover: {
      background: "#F6F6F7",
      color: "var(--carter-primary)",
      shadow: "0px 1px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#BBBFC3",
    },
    focus: {
      background: "#FFFFFF",
      color: "var(--carter-primary)",
      shadow: "0px 1px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#BBBFC3",
    },
    active: {
      background: "#F1F2F3",
      color: "var(--carter-primary)",
      shadow: "inset 0px 2px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#BBBFC3",
    },
    disabled: {
      background: "var(--carter-disabled-background)",
      color: "var(--carter-disabled-color)",
      stroke: "var(--carter-disabled-stroke)",
      shadow: "none",
    },
  },
  primary: {
    default: {
      background: "#008060",
      color: "#FFFFFF",
      shadow:
        "0px 1px 0px rgba(0, 0, 0, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.2)",
      stroke: "#008060",
    },
    hover: {
      background: "#006E52",
      color: "#FFFFFF",
      shadow: "0px 1px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#006E52",
    },
    focus: {
      background: "#008060",
      color: "#FFFFFF",
      shadow:
        "0px 1px 0px rgba(0, 0, 0, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.2)",
      stroke: "#008060",
    },
    active: {
      background: "#005E46",
      color: "#FFFFFF",
      shadow: "inset 0px 2px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#005E46",
    },
    disabled: {
      background: "#F1F1F1",
      color: "#8C9196",
      stroke: "#F1F1F1",
      shadow: "none",
    },
  },
  destructive: {
    default: {
      background: "#D82C0D",
      color: "#FFFFFF",
      shadow:
        "0px 1px 0px rgba(0, 0, 0, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.2)",
      stroke: "#D82C0D",
    },
    hover: {
      background: "#BC2200",
      color: "#FFFFFF",
      shadow:
        "0px 1px 0px rgba(0, 0, 0, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.2)",
      stroke: "#BC2200",
    },
    focus: {
      background: "#D82C0D",
      color: "#FFFFFF",
      shadow:
        "0px 1px 0px rgba(0, 0, 0, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.2)",
      stroke: "#D82C0D",
    },
    active: {
      background: "#A21B00",
      color: "#FFFFFF",
      shadow: "inset 0px 2px 0px rgba(0, 0, 0, 0.05)",
      stroke: "#A21B00",
    },
    disabled: {
      background: "#F1F1F1",
      color: "#8C9196",
      stroke: "#F1F1F1",
      shadow: "none",
    },
  },
  outline: {
    default: {
      background: "transparent",
      color: "var(--carter-primary)",
      shadow: "none",
      stroke: "#BBBFC3",
    },
    hover: {
      background: "transparent",
      color: "var(--carter-primary)",
      shadow: "none",
      stroke: "transparent",
    },
    focus: {
      background: "transparent",
      color: "var(--carter-primary)",
      shadow: "none",
      stroke: "transparent",
    },
    active: {
      background: "transparent",
      color: "var(--carter-primary)",
      shadow: "none",
      stroke: "transparent",
    },
    disabled: {
      ...colors.disabled,
      shadow: "none",
    },
  },
  plain: {
    default: {
      background: "transparent",
      color: "#2C6ECB",
      shadow: "none",
      stroke: "transparent",
    },
    hover: {
      background: "transparent",
      color: "#1F5199",
      shadow: "none",
      stroke: "transparent",
    },
    focus: {
      background: "transparent",
      color: "#2C6ECB",
      shadow: "none",
      stroke: "transparent",
    },
    active: {
      background: "transparent",
      color: "#103262",
      shadow: "none",
      stroke: "transparent",
    },
    disabled: {
      background: "transparent",
      color: "#BDC1CC",
      shadow: "none",
      stroke: "transparent",
    },
  },
  loading: {
    background: "#FAFBFB",
    color: "#BABEC3",
    shadow: "none",
    stroke: "#D2D5D8",
  },
};

// The size of the button
const sizes = {
  small: "0.3125rem 0.75rem",
  medium: "0.5rem 1rem",
  large: "0.75rem 1.5rem",
};

const Button = ({
  type,
  label,
  prefix,
  suffix,
  size,
  disabled,
  stretch,
  center,
  loading,
  onClick,
  clickable = true,
  stroke,
}) => {
  const [buttonType, setButtonType] = useState(variants.basic);
  const [buttonSize, setButtonSize] = useState(sizes.medium);

  useEffect(() => {
    if (size) setButtonSize(sizes[size]);
    setButtonType(type ? variants[type] : variants.basic);
    if (stroke) {
      variants.outline.default.stroke = stroke;
      variants.outline.hover.stroke = stroke;
      variants.outline.active.stroke = stroke;
      variants.outline.focus.stroke = stroke;
    }
  }, [size, stroke, type]);

  useEffect(() => {
    setButtonType(type ? variants[type] : variants.basic);
  }, [type]);

  return (
    <Container
      type={buttonType ? buttonType : variants[0]}
      disabled={disabled}
      size={buttonSize}
      stretch={stretch}
      center={center}
      onClick={onClick}
      style={{ pointerEvents: clickable ? "all" : "none" }}
    >
      <Inner>
        <Left>
          {prefix && <Prefix>{prefix}</Prefix>}
          <div>{label}</div>
        </Left>
        {suffix && <Suffix>{suffix}</Suffix>}
        {loading && <Loading />}
      </Inner>
    </Container>
  );
};

const Affix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 1.25rem;
  width: 1.25rem;
`;

const Prefix = styled(Affix)`
  margin-left: -0.5rem;
  margin-right: 0.5rem;
`;

const Suffix = styled(Affix)`
  margin-left: 0.5rem;
  margin-right: -0.5rem;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const Inner = styled.div`
  border: 0.0625rem solid transparent;
  border-radius: 0.25rem;

  display: flex;
  align-items: center;

  position: relative;
`;

const Container = styled.button`
  background: none;
  border: 0.125rem solid transparent;
  border-radius: 0.3125rem;
  cursor: pointer;

  font-family: ${typography.fontFamily};
  font-size: ${typography.button.size};
  font-weight: ${typography.button.weight};
  line-height: ${typography.button.lineHeight};

  outline: none;

  user-select: none;

  width: ${(props) => (props.stretch ? "100%" : "auto")};

  > ${Inner} {
    background-color: ${(props) => props.type.default.background};
    border-color: ${(props) => props.type.default.stroke};
    box-shadow: ${(props) => props.type.default.shadow};
    color: ${(props) => props.type.default.color};

    justify-content: ${(props) => (props.center ? "center" : "space-between")};

    padding: ${(props) => props.size};
    width: "100%";
  }

  &:focus {
    border-color: ${colors.focus};
  }

  &:disabled {
    cursor: auto;
  }

  &:hover ${Inner} {
    background-color: ${(props) => props.type.hover.background};
    border-color: ${(props) => props.type.hover.stroke};
    box-shadow: ${(props) => props.type.hover.shadow};
    color: ${(props) => props.type.hover.color};
  }

  &:active ${Inner} {
    background-color: ${(props) => props.type.active.background};
    border-color: ${(props) => props.type.active.stroke};
    box-shadow: ${(props) => props.type.active.shadow};
    color: ${(props) => props.type.active.color};
  }

  &:disabled ${Inner} {
    background-color: ${(props) => props.type.disabled.background};
    border-color: ${(props) => props.type.disabled.stroke};
    box-shadow: ${(props) => props.type.disabled.shadow};
    color: ${(props) => props.type.disabled.color};
  }
`;

export default Button;
